import React, { useState, useEffect } from "react";
import { Menu, X, ShoppingCart } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import { NAV_LINKS, APP_STORE_URL } from "../data/mock";
import { useCart } from "../context/CartContext";

// Hash links (e.g. "/#features") use a plain anchor for cross-page jumps;
// pure routes use react-router Link.
function NavLink({ to, className, onClick, children }) {
  if (to.includes("#")) {
    return (
      <a href={to} className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { count, setIsOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-300 border-b border-[#d7e5e9] ${
        scrolled ? "shadow-[0_0_2px_rgba(50,50,50,0.4)]" : ""
      }`}
    >
      <div className="max-w-6xl mx-auto px-5 py-[18px] flex items-center justify-between">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.label}
              to={l.to}
              className="font-serif-body capitalize text-[16px] text-[#768487] hover:text-[#68c2d5] transition-colors px-4 py-3"
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 ml-6">
          <button
            onClick={() => setIsOpen(true)}
            className="relative flex items-center gap-2 bg-[#41bf2b] hover:bg-[#39ab26] text-white font-serif-body px-5 py-2.5 rounded-[4px] text-[15px] transition-colors"
          >
            <ShoppingCart size={16} /> Cart
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#0f9fbc] text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noreferrer"
            className="bg-[#0f9fbc] hover:bg-[#008da9] text-white font-serif-body px-5 py-2.5 rounded-[4px] text-[15px] transition-colors"
          >
            Get The App
          </a>
        </div>

        <button
          className="lg:hidden text-[#3aa9ba]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-5 py-4 flex flex-col gap-3">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.label}
              to={l.to}
              onClick={() => setOpen(false)}
              className="font-serif-display text-[17px] text-[#4a4a4a] py-1"
            >
              {l.label}
            </NavLink>
          ))}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => {
                setOpen(false);
                setIsOpen(true);
              }}
              className="relative flex-1 text-center flex items-center justify-center gap-2 bg-[#41bf2b] text-white font-serif-body px-4 py-2.5 rounded-[4px]"
            >
              <ShoppingCart size={16} /> Cart
              {count > 0 && (
                <span className="ml-1 bg-[#0f9fbc] text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
            <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className="flex-1 text-center bg-[#0f9fbc] text-white font-serif-body px-4 py-2.5 rounded-[4px]">
              Get The App
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
